import { ICandidateTag, ICandidateTagHistory, ITagType } from 'app/models/Candidate/Tags';

export const UNVERIFIED_CERTIFICATION_CATEGORY = 'unverified-certification';

const TAG_HISTORY_NO_VALUE = '--';

export const buildTagChangeHistoryRows = (rows: ICandidateTagHistory[] | undefined): ICandidateTagHistory[] => {
  if (!rows?.length) {
    return [];
  }
  const chronological = [...rows].reverse();
  const result: ICandidateTagHistory[] = [];
  let previousValue = TAG_HISTORY_NO_VALUE;
  chronological.forEach(row => {
    const newValue = row.newValue ?? TAG_HISTORY_NO_VALUE;
    if (newValue.trim().toLowerCase() === previousValue.trim().toLowerCase()) {
      return;
    }
    result.push({ ...row, oldValue: previousValue, newValue });
    previousValue = newValue;
  });
  return result.reverse();
};

/** Candidate tags in this category are stored on the server but not shown in the header chip row. */
export const shouldDisplayCandidateTag = (tag: { category?: string | null }): boolean =>
  tag.category !== UNVERIFIED_CERTIFICATION_CATEGORY;

export const getPreservedCandidateTags = (tags: ICandidateTag[] | undefined): ICandidateTag[] =>
  tags?.filter(tag => !shouldDisplayCandidateTag(tag)) ?? [];

/** Legacy tag `value` keys used by GET /candidate/tags (formerly TagsV2.json labels). */
export const TAG_VALUE_BY_DESCRIPTION: Record<string, string> = {
  Hot: 'Hot',
  'Pre-Check': 'PreCheck',
  Referral: 'Referral',
  Nomadicare: 'Nomadicare',
  'Travel Therapy Mentor': 'Travel Therapy Mentor',
  'Travel Partner': 'Travel Partner',
  'Not Available: Perm': 'Not Available: Perm',
  'Not Available: Travel with Comp': 'Not Available: Travel with Comp',
  'New Grad': 'NewGrad',
  'New Grad Program': 'NewGradProgram',
  'Foreign Traveler': 'ForeignTraveler',
  'Clinical Fellowship Year': 'ClinicalFellowshipYear',
  Supervisor: 'Supervisor',
  'VA-MIL': 'VaMilExp',
  Teletherapy: 'Teletherapy',
  'Kaiser Hot': 'KaizerHot',
  'Kaiser Warm': 'KaizerWarm',
  Preceptor: 'Preceptor',
  'Profile Ready': 'ProfileReady',
};

export const getTagValueFromDescription = (description: string): string =>
  TAG_VALUE_BY_DESCRIPTION[description] ?? description;

export const mapTagTypeToLookupOption = (tagType: ITagType) => {
  const label = tagType.description;
  return {
    ...tagType,
    label,
    value: getTagValueFromDescription(label),
    name: label,
    key: tagType.id,
    disabled: false,
  };
};

/** Match candidate tag from GET /candidate/tags/new/... to a tag-type lookup row. */
export const matchesCandidateTag = (
  candidateTag: { value?: string; label?: string },
  lookupTag: { value?: string; label?: string; description?: string },
): boolean => {
  const apiValue = candidateTag.value ?? '';
  const apiLabel = candidateTag.label ?? '';
  const lookupValue = lookupTag.value ?? '';
  const lookupLabel = lookupTag.label ?? lookupTag.description ?? '';

  if (!apiValue && !apiLabel) {
    return false;
  }

  return (
    apiValue === lookupValue ||
    apiLabel === lookupLabel ||
    apiValue === lookupLabel ||
    apiLabel === lookupValue ||
    getTagValueFromDescription(apiLabel) === lookupValue ||
    getTagValueFromDescription(lookupLabel) === apiValue ||
    getTagValueFromDescription(apiLabel) === lookupLabel
  );
};

export const getTagFullDescription = (tag: {
  fullDescription?: string | null;
  FullDescription?: string | null;
}): string | null => {
  const text = tag.fullDescription ?? tag.FullDescription ?? null;
  return text?.trim() ? text.trim() : null;
};

export const mapCandidateTagToChip = (
  candidateTag: {
    value?: string;
    label?: string;
    id?: number | null;
    tagTypeId?: number | null;
    category?: string;
    isClassicTag?: boolean;
    isHidden?: boolean | null;
    fullDescription?: string | null;
    FullDescription?: string | null;
  },
  lookupTag?: ReturnType<typeof mapTagTypeToLookupOption>,
) => {
  const label = candidateTag.label ?? lookupTag?.label ?? candidateTag.value ?? '';
  const value = candidateTag.value ?? lookupTag?.value ?? label;
  const fullDescription = getTagFullDescription(candidateTag) ?? getTagFullDescription(lookupTag ?? {}) ?? null;

  if (lookupTag) {
    return { ...lookupTag, ...candidateTag, label, value, name: label, fullDescription };
  }

  return {
    ...candidateTag,
    label,
    value,
    name: label,
    fullDescription,
    key: candidateTag.tagTypeId ?? candidateTag.id ?? value,
    disabled: false,
  };
};
